import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import CheckAll from 'images/check-all.svg';
import Avatar from 'layout/atoms/Avatar/Avatar';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Loader from 'layout/atoms/Loader/Loader';
import Tabs from 'layout/atoms/Tabs/Tabs';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import { fetchPrivateMessages, fetchSpecificationAndFolderConversations } from 'store/inboxSlice';
import { selectLayout } from 'store/layoutSlice';
import { fetchNotifications, fetchUnreadNotifications, markAllNotificationsAsRead, markNotificationAsRead, selectNotifications, selectNotificationsStatus, selectUnreadNotifications, } from 'store/notificationsSlice';
import env from 'utils/env';
import styles from './NotificationsDropdown.module.scss';
function NotificationsDropdown({ children, }) {
    const { t } = useTranslation(['LAYOUT']);
    const dispatch = useDispatch();
    const location = useLocation();
    const layout = useSelector(selectLayout);
    const status = useSelector(selectNotificationsStatus);
    const unread = useSelector(selectUnreadNotifications);
    const all = useSelector(selectNotifications);
    const [activeTab, setActiveTab] = useState('all');
    const [shown, setShown] = useState([]);
    // Note: Real-time notifications are handled globally in Layout.jsx
    useEffect(() => {
        if (activeTab === 'all') {
            setShown(Array.isArray(all) ? all : []);
        }
        if (activeTab === 'unread') {
            setShown(Array.isArray(unread) ? unread : []);
        }
    }, [activeTab, all, unread]);
    useEffect(() => {
        if (status.fetch === 'idle' || status.unreadFetch === 'idle') {
            dispatch(fetchNotifications());
            dispatch(fetchUnreadNotifications());
        }
    }, [dispatch, status]);
    const handleMarkAllAsRead = () => {
        dispatch(markAllNotificationsAsRead());
    };
    const handleDropdownOpen = () => {
        // Check if we're in the inbox section
        if (location.pathname.startsWith('/inbox')) {
            // Refresh private messages and spec&folders lists
            dispatch(fetchPrivateMessages());
            dispatch(fetchSpecificationAndFolderConversations());
        }
    };
    const tabs = [
        { id: 'all', label: t('TOPBAR_NOTIFICATIONS_ALL') },
        {
            id: 'unread',
            label: t('TOPBAR_NOTIFICATIONS_UNREAD'),
            counter: unread.length > 0 ? unread.length : null,
        },
    ];
    return (React.createElement(Dropdown, { position: "menubar", closeOnClick: false, scroll: false, onOpen: handleDropdownOpen, trigger: children, fixedWidth: layout.showMenu ? '220px' : '36px' },
        React.createElement("div", { className: styles.container },
            React.createElement("header", { className: styles.header }, t('TOPBAR_NOTIFICATIONS')),
            React.createElement(Tabs, { size: "s", tabs: tabs, activeTab: activeTab, onClick: (e) => setActiveTab(e) }),
            (status.fetch === 'idle' || status.fetch === 'loading') && React.createElement(Loader, null),
            status.fetch === 'succeeded' && (React.createElement("div", { className: styles.notifications }, shown.length > 0 ? (shown.map((item) => {
                var _a, _b;
                let link = (_a = item.conversation) === null || _a === void 0 ? void 0 : _a.pageUrl;
                if (link === null || link === void 0 ? void 0 : link.includes('/users/')) {
                    link = `/inbox/private/${link.replace('/users/', '')}/conversation`;
                }
                else if (link === null || link === void 0 ? void 0 : link.includes('/channels/')) {
                    link = `/inbox/channel/${link.replace('/channels/', '')}/conversation`;
                }
                return (React.createElement("div", { key: item.id, className: styles.notification },
                    item.author && (React.createElement(Avatar, { to: link, src: item.author.avatar && `${env.static}/${item.author.avatar}`, size: "m", shape: "round", userName: item.author.username, onClick: () => dispatch(markNotificationAsRead(item.id)) })),
                    React.createElement("div", { className: styles.notificationText },
                        React.createElement("h3", null, t(`NOTIFICATION_STATUS_${item.status}`)),
                        React.createElement("p", null,
                            React.createElement("span", null, moment(item.createdDate).fromNow()),
                            item.conversation && (React.createElement(Link, { to: link, onClick: () => dispatch(markNotificationAsRead(item.id)) }, (_b = item.conversation) === null || _b === void 0 ? void 0 : _b.title)))),
                    !item.isRead && (React.createElement("button", { type: "button", className: styles.unread, onClick: () => dispatch(markNotificationAsRead(item.id)) },
                        React.createElement("p", null)))));
            })) : (React.createElement(EmptyState, { header: t('TOPBAR_NOTIFICATIONS_EMPTY_STATE'), noSubheader: true, size: "m" })))),
            React.createElement("footer", { className: styles.footer },
                React.createElement(Button, { leftIcon: React.createElement(CheckAll, null), theme: "ghost", onClick: handleMarkAllAsRead }, t('TOPBAR_NOTIFICATIONS_MARK_ALL_AS_READ')),
                React.createElement(Button, { to: "/inbox/replies/recent" }, t('TOPBAR_NOTIFICATIONS_SEE_ALL'))))));
}
export default NotificationsDropdown;
